import { useCallback, useEffect, useRef, useState } from 'react';
import type { ObservationSource, RunningState } from '@thekwallace/backtime-shared';
import timelineService from '../services/timelineService';

export interface UseRunningStateResult {
  /** Live state for the occurrence being operated, or null when nothing is running. */
  runningState: RunningState | null;
  /** True while a start/mark request is in flight. */
  pending: boolean;
  /** Last write failure, surfaced so the operator is never left guessing. */
  error: string | null;
  /** Begin operating an occurrence. `occurrenceStart` is an absolute ISO-8601 instant. */
  start: (occurrenceStart: string) => Promise<void>;
  /**
   * Record that an element actually started.
   *
   * Omit `actualStart` for a live mark — the server stamps it from its own clock,
   * which is both simpler and immune to a client whose clock is off.
   *
   * Pass it only for a retroactive correction or backfill, as an absolute ISO-8601
   * INSTANT (shared 0.9.0, plan §6.1). Callers hold times in the browser's
   * absolute-seconds frame, so they convert with `absoluteSecondsToIsoInstant`
   * before calling — an instant belongs to no timezone, so the server can store it
   * and every other client can render it without either side knowing the other's.
   */
  mark: (
    elementId: string,
    actualStart?: string,
    source?: ObservationSource
  ) => Promise<void>;
  /**
   * Retract what was recorded for one element.
   *
   * The unlock for editing history: an element the run has reached is locked, because
   * moving or retiming it would leave an observation pointing at something that changed
   * underneath it. Clearing moves it out of that set.
   *
   * Clearing the element under the playhead hands the playhead back to the previous
   * surviving mark. Append-only, so `undoLast` puts it back.
   */
  clearObservation: (elementId: string) => Promise<void>;
  /**
   * State that an element is still running.
   *
   * The statement `mark` cannot make — it says when something began, not whether it has
   * finished. Holding is what lets over-run accrue live without the app inferring it.
   */
  holdElement: (elementId: string) => Promise<void>;
  /** Re-apply the most recently undone observation. */
  redoLast: () => Promise<void>;
  /**
   * Drop the most recent observation.
   *
   * Cheap and total because every projection is a fold over an append-only log:
   * dropping the last entry restores the previous state exactly. Undoes a clear as
   * readily as a mark, which is what makes a mis-tap cheap rather than merely
   * recoverable in principle.
   */
  undoLast: () => Promise<void>;
  /**
   * End the run. The state stays readable (status `'completed'`) so viewers can
   * see how it finished; `abandon` is what clears it.
   */
  complete: () => Promise<void>;
  /** Discard the run entirely, so the occurrence can be started again cleanly. */
  abandon: () => Promise<void>;
  /** Dismiss the current error. */
  clearError: () => void;
}

/**
 * A 409 means the server has already moved on from what this client believes.
 * The server wins, so re-read rather than leaving a control that cannot work —
 * which matters most for the terminal actions, since editing stays locked until
 * the run is genuinely finished.
 */
const isConflict = (err: unknown): boolean => (err as { status?: number }).status === 409;

/**
 * Owns the running-state lifecycle for one timeline: initial fetch, live
 * `running-state-updated` subscription, and the owner-only writes.
 *
 * Writes optimistically apply whatever the API returns; the WebSocket broadcast
 * then confirms it (and is what keeps every OTHER client in step). Because the
 * payload is always the full state, a late-arriving broadcast simply overwrites
 * with the same value rather than conflicting.
 */
export function useRunningState(timelineId: string | null | undefined): UseRunningStateResult {
  const [runningState, setRunningState] = useState<RunningState | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards against a resolved request writing state after unmount / timeline switch.
  const activeTimelineRef = useRef<string | null | undefined>(timelineId);
  activeTimelineRef.current = timelineId;

  useEffect(() => {
    if (!timelineId) {
      setRunningState(null);
      return;
    }

    let cancelled = false;
    const unsubscribe = timelineService.subscribeToRunningState(timelineId, (state) => {
      if (!cancelled) setRunningState(state);
    });

    return () => {
      cancelled = true;
      unsubscribe();
      setRunningState(null);
    };
  }, [timelineId]);

  const runWrite = useCallback(
    async (
      operation: () => Promise<RunningState | null>,
      failureMessage: string,
      shouldResyncOnError?: (err: unknown) => boolean
    ) => {
      setPending(true);
      setError(null);
      try {
        // The response envelope is authoritative, INCLUDING when it is null:
        // `abandon` answers 200 with `runningState: null`, and treating that as
        // "no update" would leave the operator looking at a run the server has
        // already thrown away — with editing still locked.
        setRunningState(await operation());
      } catch (err) {
        setError(err instanceof Error ? err.message : failureMessage);
        if (shouldResyncOnError?.(err)) {
          const id = activeTimelineRef.current;
          if (id) {
            try {
              const fresh = await timelineService.getRunningState(id);
              if (activeTimelineRef.current === id) setRunningState(fresh);
            } catch {
              /* resync is best-effort; the error above is already surfaced */
            }
          }
        }
      } finally {
        setPending(false);
      }
    },
    []
  );

  const start = useCallback(
    async (occurrenceStart: string) => {
      const id = activeTimelineRef.current;
      if (!id) return;
      await runWrite(
        () => timelineService.startRunningState(id, occurrenceStart),
        'Could not start the occurrence'
      );
    },
    [runWrite]
  );

  const mark = useCallback(
    async (
      elementId: string,
      actualStart?: string,
      source?: ObservationSource
    ) => {
      const id = activeTimelineRef.current;
      if (!id) return;
      await runWrite(
        () => timelineService.markRunningTransition(id, elementId, actualStart, source),
        'Could not mark the transition',
        // Nothing is actually running — re-fetch instead of leaving the operator
        // tapping a dead button.
        isConflict
      );
    },
    [runWrite]
  );

  const complete = useCallback(async () => {
    const id = activeTimelineRef.current;
    if (!id) return;
    await runWrite(
      () => timelineService.completeRunningState(id),
      'Could not complete the occurrence',
      isConflict
    );
  }, [runWrite]);

  const abandon = useCallback(async () => {
    const id = activeTimelineRef.current;
    if (!id) return;
    await runWrite(
      () => timelineService.abandonRunningState(id),
      'Could not discard the occurrence',
      isConflict
    );
  }, [runWrite]);

  const clearError = useCallback(() => setError(null), []);

  const clearObservation = useCallback(
    async (elementId: string) => {
      const id = activeTimelineRef.current;
      if (!id) return;
      await runWrite(
        () => timelineService.clearRunningObservation(id, elementId),
        'Could not clear that element',
        isConflict
      );
    },
    [runWrite]
  );

  const holdElement = useCallback(
    async (elementId: string) => {
      const id = activeTimelineRef.current;
      if (!id) return;
      await runWrite(
        () => timelineService.holdRunningElement(id, elementId),
        'Could not hold that element',
        isConflict
      );
    },
    [runWrite]
  );

  const redoLast = useCallback(async () => {
    const id = activeTimelineRef.current;
    if (!id) return;
    await runWrite(
      () => timelineService.redoLastRunningObservation(id),
      'Could not redo',
      isConflict
    );
  }, [runWrite]);

  const undoLast = useCallback(async () => {
    const id = activeTimelineRef.current;
    if (!id) return;
    await runWrite(
      () => timelineService.undoLastRunningObservation(id),
      'Could not undo',
      isConflict
    );
  }, [runWrite]);

  return {
    runningState,
    pending,
    error,
    start,
    mark,
    clearObservation,
    holdElement,
    undoLast,
    redoLast,
    complete,
    abandon,
    clearError,
  };
}

export default useRunningState;
